import type { LegacyRef, ReactElement } from 'react';
import { forwardRef, useId, useRef } from 'react';
import { useDisclosure } from '@mantine/hooks';
import clsx from 'clsx';
import { useTranslation } from 'next-i18next/pages';
import { FiChevronRight, FiPlus } from 'react-icons/fi';

import {
  Box,
  Button,
  Collapse,
  Divider,
  Flex,
  Group,
  Menu,
  Stack,
  Tabs,
  TabsList as CoreTabsList,
  TabsTab,
  Text,
  UnstyledButton,
  VisuallyHidden,
} from '@highlight-cp/core-ui';
import { useIsSticky } from '@highlight-cp/hooks';

import { ActionIcons } from '../action-icons';
import type { ProgressStatus } from '../status-color-dot';
import { PROGRESS_STATUS_LABEL, StatusColorDot } from '../status-color-dot';
import classes from './sidebar-tabs.module.css';

interface SidebarTabsProps {
  /**
   * The children of the SidebarTabs component.
   */
  children: React.ReactNode;
  /**
   * The active tab of the SidebarTabs component.
   */
  activeTab?: string;
  /**
   * The callback function that is called when the active tab changes.
   */
  onTabChange: (value: string) => void;
  /**
   * Whether to keep the tabs mounted when they are not active.
   */
  keepMounted?: boolean;
}

const SidebarTabs = ({
  children,
  activeTab,
  onTabChange,
  keepMounted = false,
}: SidebarTabsProps) => {
  return (
    <Tabs
      h="100%"
      keepMounted={keepMounted}
      orientation="vertical"
      radius="sm"
      // `pills` for the rounded, border-free tab shape; the theme's vertical `[data-active]` rule
      // replaces its solid fill with a tint. The `default` variant's only vertical active cue is a
      // 1px border edge, too weak for a sidebar's current-section indicator.
      variant="pills"
      value={activeTab}
      onChange={(value) => value !== null && onTabChange(value)}
    >
      {children}
    </Tabs>
  );
};

interface SidebarProps {
  children: React.ReactNode;
  dataTestId?: string;
  /** Pinned to the bottom of the sidebar's full height, under a divider. */
  footer?: React.ReactNode;
}

const Sidebar = ({ children, dataTestId, footer }: SidebarProps) => {
  // we want this sidebar to fill remaining height of the page. we can't
  // set this via css because we don't know the Y position of the sidebar and the height
  // of the content next to it.
  const ref = useRef<HTMLDivElement>(null);
  const { isSticky } = useIsSticky(ref);
  return (
    <Box
      ref={ref as LegacyRef<HTMLDivElement>}
      className={clsx(classes.sidebar, isSticky && classes.sticky)}
      data-testid={dataTestId}
    >
      <CoreTabsList h="100%">
        {/* Full height only when there's a footer to hold down — otherwise the tab list's fill
            would paint past the last tab to the bottom of a sticky sidebar. */}
        <Stack gap={0} className={clsx(classes.sidebarContent, footer && classes.withFooter)}>
          {children}
          {footer && (
            <Box className={classes.footer}>
              <Divider mb="xs" />
              {footer}
            </Box>
          )}
        </Stack>
      </CoreTabsList>
    </Box>
  );
};
Sidebar.displayName = 'SidebarTabs.Sidebar';

interface SidebarTabsListProps {
  children: React.ReactNode;
}

const TabsList = ({ children }: SidebarTabsListProps) => {
  return (
    <Stack gap={0} className={classes.stack}>
      {children}
    </Stack>
  );
};
TabsList.displayName = 'SidebarTabs.TabsList';

interface MenuProps {
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  label?: string;
  items: {
    color?: string;
    label: string;
    disabled?: boolean;
    onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
  }[];
}

interface TabMenuProps {
  menu: MenuProps;
  label?: string;
}

interface SidebarTabsTabProps {
  /**
   * The tab's accessible name, for rich children — without it the name is computed from the whole
   * label, so any summary lines under the title are read out as part of it. String children name
   * themselves.
   */
  ariaLabel?: string;
  captionText?: string;
  /**
   * What rich children say beneath the title, as text. `role="tab"` prunes its own subtree from the
   * accessibility tree, so summary lines rendered inside it reach nobody — this is how they are
   * described instead, without joining the name.
   */
  description?: string;
  children: string | React.ReactNode;
  dataTestId?: string;
  disabled?: boolean;
  /** Decorative only — status belongs in `status`, which renders the dot the design calls for. */
  icon?: ReactElement;
  onClick?: () => void;
  menu?: MenuProps;
  /** Renders a `StatusColorDot` after the label. */
  status?: ProgressStatus;
  value?: string;
}

const TabMenu = ({ menu, label }: TabMenuProps) => {
  return (
    <Menu offset={1} position="bottom" withinPortal withArrow>
      <Menu.Target>
        {/* Box, not Menu.Label: this only positions the trigger, and Menu.Label is a dropdown
            section heading. The class restates the padding the label contributed — the rest of
            what it brought (dimmed, `xs`, medium, `cursor: default`) never applied, since the sole
            child is an ActionIcon carrying its own. */}
        <Box className={classes.menuLabel}>
          {/* Box carries the class: the reveal is a parent-hover slide/fade, which has no prop
              equivalent, and the locked ActionIcon takes no className. */}
          <Box className={classes.menuIcon} display="inline-flex">
            <ActionIcons.MenuVertical
              c="dimmed"
              size={24}
              ariaLabel={label ? `${label} action menu` : undefined}
              onClick={(e) => {
                menu.onClick?.(e);
              }}
            />
          </Box>
        </Box>
      </Menu.Target>

      <Menu.Dropdown>
        {Array.isArray(menu?.items) &&
          menu?.items.map((item) => (
            <Menu.Item
              color={item.color}
              disabled={item.disabled}
              key={item.label}
              onClick={item.onClick}
            >
              {item.label}
            </Menu.Item>
          ))}
      </Menu.Dropdown>
    </Menu>
  );
};

/**
 * A tab label with its status dot trailing the last word. The label wraps to whatever height it
 * needs rather than clamping: a clamp truncates the dot away with the overflow, and pinning the dot
 * clear of the clamp strands it at the column edge whenever the last line falls short of it.
 */
const TabLabel = ({ children, status }: { children: string; status?: ProgressStatus }) => (
  // `inherit`: the tab sets its own font-size and 18px line-height, which Text would otherwise pin.
  <Text span inherit>
    {children}
    {/* `decorative` because the tab carries the status on `aria-describedby`; a labelled dot here
        would have it announced twice. `StatusColorDot` is inline-block and brings its own gap. */}
    {status && <StatusColorDot decorative status={status} />}
  </Text>
);

const Tab = ({
  ariaLabel,
  captionText,
  children,
  dataTestId,
  description,
  disabled,
  icon,
  onClick,
  menu,
  status,
  value,
}: SidebarTabsTabProps) => {
  const { t } = useTranslation();
  const isContentString = typeof children === 'string';
  const label = isContentString ? t(children) : undefined;
  // Rich children carry their own text, so the caller has to say what names the tab.
  const accessibleName = label ?? ariaLabel;
  const statusDescriptionId = useId();
  const summaryDescriptionId = useId();
  const describedBy =
    [status && statusDescriptionId, description && summaryDescriptionId]
      .filter(Boolean)
      .join(' ') || undefined;

  // Usually tabs will have values, but there's use cases where we want to render a button in
  // this sidebar instead of a tab.
  return (
    <Group className={classes.tabWrapper} gap={2}>
      {value ? (
        // render a tab if it has a value
        <>
          {/* No `c` override: the `pills` variant sets tab color through `--tab-color`, and a
              style-prop color would land as an inline style and outrank it when active. */}
          <TabsTab
            aria-describedby={describedBy}
            // Rich children name the tab by their whole content — every summary line included —
            // unless the caller names it themselves.
            aria-label={isContentString ? children : ariaLabel}
            disabled={disabled}
            flex={1}
            fw={600}
            fz="sm"
            value={value}
            mb={5}
            p="md"
            data-testid={dataTestId}
          >
            <Box className={classes.content}>
              <Flex gap="xs" align="center">
                {icon && <Box className={classes.icon}>{icon}</Box>}
                {/* Rich children own their whole label, dot included — see the audience tabs, which
                    place it inside their own title so it trails the name rather than the block.
                    `status` still reaches the description below either way. */}
                {isContentString ? <TabLabel status={status}>{children}</TabLabel> : children}
              </Flex>
            </Box>
            {captionText && (
              // `dimmed` clears AA on both fills this tab takes: 5.9:1 on the active tab, 5.5:1 on
              // the paper behind the inactive ones.
              <Text size="xs" c="dimmed" pl="sm" pt={4}>
                {t(captionText)}
              </Text>
            )}
          </TabsTab>
          {status && (
            // Described, not named: the accessible name is what tests and `aria-label` consumers
            // match on, so it stays the label alone rather than changing as a step moves from draft
            // to ready. Rendered outside the tab because `role="tab"` makes its children
            // presentational, which would prune this text from the tree.
            <VisuallyHidden id={statusDescriptionId}>
              {t(PROGRESS_STATUS_LABEL[status])}
            </VisuallyHidden>
          )}
          {/* Outside the tab for the same reason, and already translated by the caller that wrote
              the summary it repeats. */}
          {description && <VisuallyHidden id={summaryDescriptionId}>{description}</VisuallyHidden>}
          {menu && <TabMenu label={accessibleName} menu={menu} />}
        </>
      ) : (
        // render a button if it doesn't have a value
        <Button
          leftSection={icon}
          onClick={onClick}
          variant="subtle"
          c="dimmed"
          data-testid={dataTestId}
          flex={1}
          radius="sm"
          justify="flex-start"
          disabled={disabled}
        >
          {label ? (
            // No clamp: Mantine's Button label is `white-space: nowrap`, so a second line is
            // unreachable and it clips against the label's own `overflow: hidden`.
            <Text span inherit title={label}>
              {label}
            </Text>
          ) : (
            children
          )}
        </Button>
      )}
    </Group>
  );
};
Tab.displayName = 'SidebarTabs.Tab';

interface SidebarTabsSectionProps {
  /** Sits on the label row, right-aligned — an "add" action for what the section lists. */
  action?: React.ReactNode;
  children: React.ReactNode;
  label: string;
}

/**
 * A labelled group of tabs. The label row is the only thing separating one section from the next —
 * no rules above or below it; the footer's divider is the sidebar's one horizontal line.
 */
const Section = ({ action, children, label }: SidebarTabsSectionProps) => {
  const { t } = useTranslation();
  return (
    <>
      <Group justify="space-between" gap="xs" wrap="nowrap" pt={5} pb={5}>
        <Text size="xs" fw={600} c="dimmed">
          {t(label.toUpperCase())}
        </Text>
        {action}
      </Group>
      {children}
    </>
  );
};
Section.displayName = 'SidebarTabs.Section';

interface SidebarTabsSectionActionProps {
  /** Names the action for assistive tech when its label alone doesn't, e.g. "Add product". */
  'aria-label'?: string;
  /** The action's label, translated here as `Tab`'s string children are. */
  children: string;
  disabled?: boolean;
  onClick: () => void;
}

/**
 * The "add one of these" control for a `Section`, passed to its `action` slot. Its own component so
 * every step's create action reads the same rather than each call site restating the recipe — the
 * plus is part of that recipe, not a call-site choice.
 */
const SectionAction = forwardRef<HTMLButtonElement, SidebarTabsSectionActionProps>(
  ({ 'aria-label': ariaLabel, children, disabled, onClick }, ref) => {
    const { t } = useTranslation();
    return (
      // `c` as well as `color`: the `subtle` variant's own text colour is the dimmed default, so the
      // primary tint has to be set on the label too (EPD-1393) — but only while it can be pressed,
      // since that same `c` outranks the disabled colour and leaves a dead control reading as live
      // (EPD-1424).
      <Button
        aria-label={ariaLabel}
        c={disabled ? undefined : 'var(--mantine-primary-color-filled)'}
        color="primary"
        disabled={disabled}
        leftSection={<FiPlus />}
        onClick={onClick}
        ref={ref}
        size="compact-xs"
        variant="subtle"
      >
        {t(children)}
      </Button>
    );
  },
);
SectionAction.displayName = 'SidebarTabs.SectionAction';

interface SidebarTabsAccordionSectionProps {
  children: React.ReactNode;
  label: string;
  menu?: {
    onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
    label?: string;
    items: {
      color?: string;
      label: string;
      disabled?: boolean;
      onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
    }[];
  };
}

const AccordionSection = ({ children, label, menu }: SidebarTabsAccordionSectionProps) => {
  const [opened, { toggle }] = useDisclosure(true);
  const panelId = useId();
  return (
    <Box className={classes.accordionItem}>
      <Flex justify="space-between" align="center" className={classes.accordionItemContent}>
        <UnstyledButton
          className={classes.accordionControl}
          onClick={toggle}
          aria-expanded={opened}
          aria-controls={panelId}
          title={label}
        >
          <FiChevronRight
            className={clsx(classes.accordionChevron, opened && classes.accordionChevronOpen)}
          />
          <Box className={classes.accordionLabel}>
            <Text size="xs" fw={600} c="dimmed" truncate>
              {label}
            </Text>
          </Box>
        </UnstyledButton>
        {menu && <TabMenu label={label} menu={menu} />}
      </Flex>
      <Collapse expanded={opened} id={panelId}>
        <Box className={classes.accordionPanel}>
          <Box className={classes.accordionContent}>{children}</Box>
        </Box>
      </Collapse>
    </Box>
  );
};
AccordionSection.displayName = 'SidebarTabs.AccordionSection';

SidebarTabs.AccordionSection = AccordionSection;
SidebarTabs.Section = Section;
SidebarTabs.SectionAction = SectionAction;
SidebarTabs.Tab = Tab;
SidebarTabs.TabMenu = TabMenu;
SidebarTabs.TabsList = TabsList;
SidebarTabs.Sidebar = Sidebar;
export default SidebarTabs;
